import {
    ChangeDetectionStrategy,
    Component,
    OnDestroy,
    OnInit,
    ViewEncapsulation,
} from '@angular/core';
import {
    FormBuilder,
    FormGroup,
} from '@angular/forms';
import {
    AppStateFacade,
    Setting,
} from '@app-state';
import { UiModalComponent } from '@components/ui-modal/ui-modal.component';
import {
    IQueueItemDto,
    IQueueItemDtoWithItemId,
    ModalResultType,
    QueueItemPriority,
} from '@models';
import { TranslateService } from '@ngx-translate/core';
import {
    FileDownloadService,
    QueueItemService,
} from '@services';
import { ISuggestValue } from '@uipath/angular/components/ui-suggest/models';
import { readFileContent } from '@utils/file.utils';
import UiValidators, { UI_QUEUE_ITEM_REFERENCE_MAX_LENGTH } from '@validators';

import * as moment from 'moment';
import {
    BehaviorSubject,
    Subject,
} from 'rxjs';
import { finalize } from 'rxjs/operators';

export enum TransactionFormControl {
    reference = 'reference',
    priority = 'priority',
    dueDate = 'dueDate',
    deferDate = 'deferDate',
    specificContent = 'specificContent',
    keepProgress = 'keepProgress',
}

@Component({
    templateUrl: './transaction-upsert.modal.html',
    styleUrls: ['./transaction-upsert.modal.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [QueueItemService],
})
export class TransactionUpsertModalComponent extends UiModalComponent<IQueueItemDtoWithItemId> implements OnInit, OnDestroy {
    public transactionForm!: FormGroup;
    public TransactionFormControl = TransactionFormControl;

    public submitting$ = new BehaviorSubject<boolean>(false);
    public isFileUploading$ = new BehaviorSubject<boolean>(false);

    public get priorityTypes(): ISuggestValue[] {
        return [QueueItemPriority.high, QueueItemPriority.normal, QueueItemPriority.low]
            .map(priority => ({
                id: priority,
                text: this._translate.instant(`PriorityType_${priority}`),
            }));
    }

    public get selectedType() {
        return this.priorityTypes.filter(priorityType => priorityType.id === this.context.data.priority);
    }

    public get slaDefaultPriority() {
        return this.priorityTypes.filter(priorityType => priorityType.id === QueueItemPriority.high);
    }

    private _formBuilder = this._injector.get(FormBuilder);
    private _queueService = this._injector.get<QueueItemService>(QueueItemService);
    private _downloadService = this._injector.get<FileDownloadService>(FileDownloadService);
    private _translate = this._injector.get<TranslateService>(TranslateService);
    private _appState = this._injector.get<AppStateFacade>(AppStateFacade);
    private _destroyed$ = new Subject();

    private _timezone = this._appState.settings.get(Setting.timezone);

    ngOnInit() {
        this.transactionForm = this._formBuilder.group(this._transactionFormConfig);

        if (this.isClone) {
            this.transactionForm.markAsDirty();
        }
    }

    ngOnDestroy() {
        this._destroyed$.next();
        this._destroyed$.complete();
    }

    public onSubmit() {
        this.submitting$.next(true);

        this._submit$.pipe(
            finalize(() => {
                this.submitting$.next(false);
            }),
        ).subscribe(
            () => {
                this._snack.success(this._translate.instant('save_successful'));
                this._dialogRef.close({
                    type: this.context.isDuplicate ? ModalResultType.Add : ModalResultType.Update,
                });
            },
            error => {
                if (error.status === 403) {
                    this._snack.error(
                        this._translate.instant(`snack_ou_access_denied`),
                    );
                    this._dialogRef.close({
                        type: ModalResultType.Failed,
                    });
                }
            },
        );
    }

    public get downloadLink() {
        return `/transaction-item-${this.context.data.id}.json`;
    }

    public get isClone() {
        return this.context.isDuplicate;
    }

    public get isSlaEnabled() {
        return this.context.data.slaEnabled;
    }

    public get isSubmitDisabled() {
        return this.submitting$.value ||
            this.isFileUploading$.value ||
            !this.transactionForm ||
            // for !dirty, we want to disable if it was an edit
            (!this.transactionForm.dirty && !this.context.isDuplicate) ||
            !this.transactionForm.valid;
    }

    public downloadItemData(event: MouseEvent) {
        event.preventDefault();
        this._downloadService.downloadStringAsFile(
            `transaction-item-${this.context.data.id}.json`,
            'application/json',
            JSON.stringify(this.transactionForm.get(TransactionFormControl.specificContent)!.value),
        );
    }

    public async specificDataChanged(files: File[]) {
        this.isFileUploading$.next(true);

        try {
            const resultAsString = await readFileContent(files[0]);
            const result = JSON.parse(resultAsString);
            this.transactionForm.get(TransactionFormControl.specificContent)!.setValue(result);
            this.transactionForm.get(TransactionFormControl.specificContent)!.markAsDirty();
        } catch (e) {
            this._snack.error(
                this._translate.instant(`transaction_upsert_invalid_json`),
            );
            return;
        } finally {
            this.isFileUploading$.next(false);
        }
    }

    private get _submit$() {
        if (!this.transactionForm) { throw new Error('Form is undefined'); }

        this.submitting$.next(true);

        let progress = this.context.data.progress;
        if (
            this.context.isDuplicate ||
            this.transactionForm.get(TransactionFormControl.keepProgress)!.value === false
        ) {
            progress = undefined;
        }

        const utcOffset = this._timezone ? moment.tz(this._timezone).utcOffset() : 0;
        const formValues = this.transactionForm.value;
        delete formValues['keepProgress'];

        const dueDateControl = this.transactionForm.get('dueDate')!;
        const deferDateControl = this.transactionForm.get('deferDate')!;

        const payload: IQueueItemDto = {
            ...formValues,
            name: this.context.data.name,
            priority: this.transactionForm.get(TransactionFormControl.priority)!.value[0].id,
            // adjust timezone manually before it is converted to UTC
            dueDate: dueDateControl.value && !this.context.data.dueDate ?
                moment(dueDateControl.value).subtract(utcOffset, 'minutes') : dueDateControl.value,
            deferDate: deferDateControl.value && !this.context.data.deferDate ?
                moment(deferDateControl.value).subtract(utcOffset, 'minutes') : deferDateControl.value,
            progress,
        };

        if (this.isClone) {
            return this._queueService.addQueueItem(payload);
        }

        return this._queueService.updateQueueItem(this.context.data.id, payload);
    }

    private get _transactionFormConfig() {
        return {
            [TransactionFormControl.reference]: [
                this.context.data.reference,
                UiValidators.maxLength(UI_QUEUE_ITEM_REFERENCE_MAX_LENGTH),
            ],
            [TransactionFormControl.priority]: [
                this.isSlaEnabled ? this.slaDefaultPriority : this.selectedType,
                UiValidators.required(),
            ],
            [TransactionFormControl.dueDate]: [
                this.context.data.dueDate && this._timezone ?
                    this.context.data.dueDate.tz(this._timezone) : this.context.data.dueDate,
            ],
            [TransactionFormControl.deferDate]: [
                this.context.data.deferDate && this._timezone ?
                    this.context.data.deferDate.tz(this._timezone) : this.context.data.deferDate,
            ],
            [TransactionFormControl.specificContent]: [
                this.context.data.specificContent,
            ],
            [TransactionFormControl.keepProgress]: [
                true,
            ],
        };
    }
}
